// CMS Advanced Analytics Dashboard
import React, { useState, useEffect } from 'react';
import { useCMS } from './CMSContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { BarChart3, TrendingUp, TrendingDown, Users, CreditCard, Ticket, ArrowRight, Search, Euro, Building2, Download, RefreshCw, User, MapPin, Globe, Star, Info, Check, AlertCircle, ExternalLink, Eye, Share2, MessageCircle, Twitter, Facebook, Copy, Crown } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const CMSAnalytics = () => {
  const { token } = useCMS();
  const [period, setPeriod] = useState('daily');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState(null);
  const [userGrowth, setUserGrowth] = useState(null);
  const [passPerformance, setPassPerformance] = useState(null);
  const [bookingFunnel, setBookingFunnel] = useState(null);
  const [hotelSearches, setHotelSearches] = useState(null);
  const [searchDateFilter, setSearchDateFilter] = useState('week');
  const [showOtaModal, setShowOtaModal] = useState(false);
  const [selectedOtaData, setSelectedOtaData] = useState(null);
  
  // OTA Scraping Analytics state
  const [otaScrapeResults, setOtaScrapeResults] = useState([]);
  const [otaScrapeAnalytics, setOtaScrapeAnalytics] = useState(null);
  const [otaScrapeLoading, setOtaScrapeLoading] = useState(false);
  const [selectedScrapeDetail, setSelectedScrapeDetail] = useState(null);
  const [showScrapeDetailModal, setShowScrapeDetailModal] = useState(false);
  const [scrapeDestFilter, setScrapeDestFilter] = useState('');
  const [scrapeArchive, setScrapeArchive] = useState(false);
  
  // Share Analytics state
  const [shareData, setShareData] = useState(null);
  const [shareConversions, setShareConversions] = useState(null);
  const [shareDays, setShareDays] = useState(30);
  const [shareLoading, setShareLoading] = useState(false);

  useEffect(() => {
    fetchAllAnalytics();
  }, [period, days]);
  
  useEffect(() => {
    fetchOtaScrapeData();
  }, [scrapeDestFilter, scrapeArchive]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const [revRes, userRes, passRes, funnelRes, searchRes] = await Promise.all([
        fetch(`${API}/cms/analytics/revenue-trend?period=${period}&days=${days}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/cms/analytics/user-growth?period=${period}&days=${days}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/cms/analytics/pass-performance?days=${days}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/cms/analytics/booking-funnel?days=${days}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/cms/analytics/hotel-searches?date_filter=${searchDateFilter}&limit=100`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (revRes.ok) setRevenueData(await revRes.json());
      if (userRes.ok) setUserGrowth(await userRes.json());
      if (passRes.ok) setPassPerformance(await passRes.json());
      if (funnelRes.ok) setBookingFunnel(await funnelRes.json());
      if (searchRes.ok) setHotelSearches(await searchRes.json());
      
      // Also fetch OTA scrape data on initial load
      fetchOtaScrapeData();
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchOtaScrapeData = async () => {
    setOtaScrapeLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '100',
        search_archive: scrapeArchive.toString()
      });
      if (scrapeDestFilter) {
        params.append('destination', scrapeDestFilter);
      }
      
      const [resultsRes, analyticsRes] = await Promise.all([
        fetch(`${API}/admin/ota-scraping/results?${params.toString()}`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }),
        fetch(`${API}/admin/ota-scraping/analytics`, { 
          headers: { Authorization: `Bearer ${token}` } 
        })
      ]);
      
      if (resultsRes.ok) {
        const data = await resultsRes.json();
        setOtaScrapeResults(data.results || []);
      }
      if (analyticsRes.ok) {
        setOtaScrapeAnalytics(await analyticsRes.json());
      }
    } catch (error) {
      console.error('Failed to load OTA scrape data:', error);
    } finally {
      setOtaScrapeLoading(false);
    }
  };
  
  const exportOtaScrapeCSV = async () => {
    try {
      const params = new URLSearchParams({
        search_archive: scrapeArchive.toString()
      });
      if (scrapeDestFilter) {
        params.append('destination', scrapeDestFilter);
      }
      
      const response = await fetch(`${API}/admin/ota-scraping/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ota_pricing_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('CSV gedownload');
      }
    } catch (error) {
      toast.error('Export mislukt');
    }
  };
  
  // Share Analytics functions
  const fetchShareAnalytics = async (d = shareDays) => {
    setShareLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sharesRes, convRes] = await Promise.all([
        fetch(`${API}/cms/analytics/shares?days=${d}`, { headers }),
        fetch(`${API}/cms/analytics/shares/conversions?days=${d}`, { headers })
      ]);
      if (sharesRes.ok) setShareData(await sharesRes.json());
      if (convRes.ok) setShareConversions(await convRes.json());
    } catch (error) {
      console.error('Failed to load share analytics:', error);
    } finally {
      setShareLoading(false);
    }
  };

  const SHARE_METHOD_COLORS = {
    whatsapp: '#25D366',
    twitter: '#1DA1F2',
    facebook: '#1877F2',
    copy: '#6B7280',
    native: '#8B5CF6'
  };

  const SHARE_TYPE_COLORS = {
    savings: '#10b981',
    pass: '#f59e0b',
    booking: '#3b82f6',
    referral: '#ef4444'
  };

  // OTA Platform styling
  const getOtaColor = (ota) => {
    const colors = {
      'Booking.com': '#003580',
      'Kayak': '#ff690f',
      'Trivago': '#00b4cc',
      'Hotels.com': '#d32f2f',
      'Expedia': '#ffc107',
      'Google Hotels': '#4285f4',
      'Market Low': '#9333ea',
      'Market Price': '#ec4899'
    };
    return colors[ota] || '#64748b';
  };

  const StatCard = ({ title, value, change, icon: Icon, positive }) => (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-400">{title}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
            {change !== undefined && (
              <p className={`text-sm flex items-center gap-1 ${positive ? 'text-green-400' : 'text-red-400'}`}>
                {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {change}%
              </p>
            )}
          </div>
          <Icon className="h-8 w-8 text-slate-500" />
        </div>
      </CardContent>
    </Card>
  );

  if (loading && !revenueData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cms-analytics">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="h-6 w-6" /> Advanced Analytics
          </h1>
          <p className="text-slate-400">Deep insights into your business metrics</p>
        </div>
        <div className="flex gap-4">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 bg-slate-800 border-slate-600">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-32 bg-slate-800 border-slate-600">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 Days</SelectItem>
              <SelectItem value="30">30 Days</SelectItem>
              <SelectItem value="90">90 Days</SelectItem>
              <SelectItem value="365">1 Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Revenue" 
          value={`€${revenueData?.current_total?.toLocaleString() || 0}`}
          change={revenueData?.growth_percentage}
          icon={CreditCard}
          positive={revenueData?.growth_percentage >= 0}
        />
        <StatCard 
          title="New Users" 
          value={userGrowth?.total_new_users || 0}
          icon={Users}
        />
        <StatCard 
          title="Active Passes" 
          value={passPerformance?.active_passes || 0}
          icon={Ticket}
        />
        <StatCard 
          title="Conversion Rate" 
          value={`${userGrowth?.conversion_rate || 0}%`}
          icon={TrendingUp}
        />
      </div>

      <Tabs defaultValue="revenue" className="space-y-4">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="revenue">Revenue Trend</TabsTrigger>
          <TabsTrigger value="users">User Growth</TabsTrigger>
          <TabsTrigger value="passes">Pass Performance</TabsTrigger>
          <TabsTrigger value="funnel">Booking Funnel</TabsTrigger>
          <TabsTrigger value="hotel-searches">Hotel Searches</TabsTrigger>
          <TabsTrigger value="ota-pricing" className="text-green-400">OTA Pricing</TabsTrigger>
          <TabsTrigger value="shares" className="text-purple-400" onClick={() => { if (!shareData) fetchShareAnalytics(); }}>Share Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Revenue Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueData?.data_points || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {revenueData?.insights && (
                <div className="mt-4 p-4 bg-slate-900 rounded-lg">
                  <h4 className="text-sm font-semibold text-white mb-2">Insights</h4>
                  <ul className="text-sm text-slate-400 space-y-1">
                    {revenueData.insights.map((insight, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <ArrowRight className="h-3 w-3 text-blue-400" />
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">User Growth & Conversion</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={userGrowth?.data_points || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="new_users" fill="#3b82f6" name="New Users" />
                    <Bar dataKey="with_pass" fill="#10b981" name="With Pass" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {userGrowth?.insights && (
                <div className="mt-4 p-4 bg-slate-900 rounded-lg">
                  <h4 className="text-sm font-semibold text-white mb-2">Insights</h4>
                  <ul className="text-sm text-slate-400 space-y-1">
                    {userGrowth.insights.map((insight, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <ArrowRight className="h-3 w-3 text-green-400" />
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="passes">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Pass Sales by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={passPerformance?.sales_by_type || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="count"
                        nameKey="type"
                        label={({ type, count }) => `${type}: ${count}`}
                      >
                        {(passPerformance?.sales_by_type || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Pass Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-slate-900 rounded-lg">
                  <span className="text-slate-400">Active Passes</span>
                  <span className="text-xl font-bold text-white">{passPerformance?.active_passes || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-900 rounded-lg">
                  <span className="text-slate-400">Bookings with Pass</span>
                  <span className="text-xl font-bold text-white">{passPerformance?.pass_bookings || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-yellow-900/30 rounded-lg border border-yellow-700">
                  <span className="text-yellow-400">Expiring in 30 Days</span>
                  <span className="text-xl font-bold text-yellow-400">{passPerformance?.expiring_in_30_days || 0}</span>
                </div>
                {passPerformance?.insights && (
                  <div className="pt-4 border-t border-slate-700">
                    <h4 className="text-sm font-semibold text-white mb-2">Opportunities</h4>
                    <ul className="text-sm text-slate-400 space-y-1">
                      {passPerformance.insights.map((insight, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <ArrowRight className="h-3 w-3 text-yellow-400" />
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="funnel">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Booking Funnel</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-slate-900 rounded-lg">
                    <p className="text-3xl font-bold text-blue-400">{bookingFunnel?.funnel?.searches || 0}</p>
                    <p className="text-sm text-slate-400">Searches</p>
                  </div>
                  <div className="text-center p-4 bg-slate-900 rounded-lg">
                    <p className="text-3xl font-bold text-cyan-400">{bookingFunnel?.funnel?.prebooks || 0}</p>
                    <p className="text-sm text-slate-400">Prebooks</p>
                    <p className="text-xs text-slate-500">{bookingFunnel?.conversion_rates?.search_to_prebook || 0}% conv.</p>
                  </div>
                  <div className="text-center p-4 bg-slate-900 rounded-lg">
                    <p className="text-3xl font-bold text-green-400">{bookingFunnel?.funnel?.completed_bookings || 0}</p>
                    <p className="text-sm text-slate-400">Completed</p>
                    <p className="text-xs text-slate-500">{bookingFunnel?.conversion_rates?.prebook_to_booking || 0}% conv.</p>
                  </div>
                  <div className="text-center p-4 bg-slate-900 rounded-lg">
                    <p className="text-3xl font-bold text-red-400">{bookingFunnel?.funnel?.cancelled || 0}</p>
                    <p className="text-sm text-slate-400">Cancelled</p>
                  </div>
                </div>
                
                <div className="p-4 bg-slate-900 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Overall Conversion Rate</span>
                    <span className="text-2xl font-bold text-green-400">{bookingFunnel?.conversion_rates?.overall || 0}%</span>
                  </div>
                </div>

                {bookingFunnel?.insights && (
                  <div className="p-4 bg-slate-900 rounded-lg">
                    <h4 className="text-sm font-semibold text-white mb-2">Insights</h4>
                    <ul className="text-sm text-slate-400 space-y-1">
                      {bookingFunnel.insights.map((insight, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <ArrowRight className="h-3 w-3 text-green-400" />
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hotel Searches Tab - Price Analysis */}
        <TabsContent value="hotel-searches">
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Search className="h-8 w-8 text-blue-400" />
                    <div>
                      <p className="text-2xl font-bold text-white">{hotelSearches?.total_searches || 0}</p>
                      <p className="text-sm text-slate-400">Total Searches</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Euro className="h-8 w-8 text-green-400" />
                    <div>
                      <p className="text-2xl font-bold text-white">{hotelSearches?.average_markup_percentage || 0}%</p>
                      <p className="text-sm text-slate-400">Avg Markup</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-8 w-8 text-yellow-400" />
                    <div>
                      <p className="text-2xl font-bold text-white">{hotelSearches?.top_hotels?.length || 0}</p>
                      <p className="text-sm text-slate-400">Unique Hotels</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="h-8 w-8 text-cyan-400" />
                    <div>
                      <p className="text-lg font-bold text-white truncate" title={hotelSearches?.top_hotels?.[0]?.name}>
                        {hotelSearches?.top_hotels?.[0]?.name?.substring(0, 15) || 'N/A'}...
                      </p>
                      <p className="text-sm text-slate-400">Top Searched</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top Searched Hotels */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Populairste Bestemmingen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {hotelSearches?.top_destinations?.map((dest, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-slate-900 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-slate-500 w-6">#{i + 1}</span>
                        <span className="text-white font-medium">{dest.name}</span>
                      </div>
                      <span className="text-blue-400 font-bold">{dest.searches} zoekopdrachten</span>
                    </div>
                  ))}
                  {(!hotelSearches?.top_destinations || hotelSearches.top_destinations.length === 0) && (
                    <p className="text-slate-400 text-center py-4">Nog geen zoekopdrachten geregistreerd</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Searches with Pricing - UPDATED */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Euro className="h-5 w-5" />
                      Hotel Zoekopdrachten - Prijsanalyse
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Gedetailleerde analyse met OTA prijsvergelijking
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Select value={searchDateFilter} onValueChange={(v) => { setSearchDateFilter(v); }}>
                      <SelectTrigger className="w-40 bg-slate-900 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="today">Vandaag</SelectItem>
                        <SelectItem value="week">Laatste 7 dagen</SelectItem>
                        <SelectItem value="month">Laatste 30 dagen</SelectItem>
                        <SelectItem value="year">Laatste jaar</SelectItem>
                        <SelectItem value="all">Alles</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={fetchAllAnalytics} className="bg-slate-900 border-slate-600">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="bg-slate-900 border-slate-600"
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API}/admin/hotel-search-analytics/export?date_filter=${searchDateFilter}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          if (res.ok) {
                            const blob = await res.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `hotel_search_analytics_${searchDateFilter}.csv`;
                            a.click();
                            toast.success('CSV gedownload');
                          }
                        } catch (e) {
                          toast.error('Export mislukt');
                        }
                      }}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* API Status Banner */}
                {hotelSearches && (
                  <div className={`mb-4 p-3 rounded-lg ${hotelSearches.api_configured ? 'bg-green-900/30 border border-green-700' : 'bg-yellow-900/30 border border-yellow-700'}`}>
                    <div className="flex items-center gap-2">
                      {hotelSearches.api_configured ? (
                        <>
                          <Check className="h-4 w-4 text-green-400" />
                          <span className="text-green-400 text-sm">OTA Price API Actief - Echte prijzen worden opgehaald</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-4 w-4 text-yellow-400" />
                          <span className="text-yellow-400 text-sm">OTA Price API niet geconfigureerd - Geschatte prijzen worden getoond</span>
                          <a href="https://rapidapi.com/manthankool/api/makcorps-hotel-price-comparison" target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400 hover:underline text-sm flex items-center gap-1">
                            API Key Aanvragen <ExternalLink className="h-3 w-3" />
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Datum</TableHead>
                        <TableHead className="text-slate-400">Klant</TableHead>
                        <TableHead className="text-slate-400">IP/Land</TableHead>
                        <TableHead className="text-slate-400">Hotel</TableHead>
                        <TableHead className="text-slate-400">Bestemming</TableHead>
                        <TableHead className="text-slate-400 text-right">Nett (SH)</TableHead>
                        <TableHead className="text-slate-400 text-right">Markup (16%)</TableHead>
                        <TableHead className="text-slate-400 text-right">Freestays</TableHead>
                        <TableHead className="text-slate-400 text-right">FS -15%</TableHead>
                        <TableHead className="text-slate-400 text-right">BTW</TableHead>
                        <TableHead className="text-slate-400 text-right">OTA Est.</TableHead>
                        <TableHead className="text-slate-400">OTA</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {hotelSearches?.searches?.slice(0, 50).map((search, i) => (
                        <TableRow key={i} className="border-slate-700">
                          <TableCell className="text-slate-400 text-xs font-mono">
                            {search.timestamp ? new Date(search.timestamp).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3 text-slate-500" />
                              <span className={`text-sm ${search.client_name === 'Onbekend' ? 'text-slate-500' : 'text-white'}`}>
                                {search.client_name || 'Onbekend'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs">
                            <div className="text-slate-400 font-mono">{search.client_ip || '-'}</div>
                            <div className="text-slate-500">{search.client_country || '-'}</div>
                          </TableCell>
                          <TableCell className="text-white font-medium max-w-[150px] truncate" title={search.hotel_name}>
                            <div className="flex items-center gap-1">
                              {search.hotel_name}
                              {search.star_rating > 0 && (
                                <div className="flex">
                                  {[...Array(Math.min(search.star_rating, 5))].map((_, j) => (
                                    <Star key={j} className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                  ))}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-slate-500" />
                              <span className="text-slate-300">{search.destination || '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-cyan-400 font-mono text-sm">
                            €{search.api_prices?.nett_price?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell className="text-right text-green-400 font-mono text-sm">
                            €{search.markup_amount?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell className="text-right text-white font-mono font-bold text-sm">
                            €{search.display_prices?.freestays_total?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell className="text-right text-blue-400 font-mono text-sm">
                            €{search.display_prices?.freestays_discounted?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell className="text-right text-orange-400 font-mono text-sm">
                            €{search.btw_amount?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell className="text-right text-red-400 font-mono text-sm">
                            €{search.display_prices?.ota_estimated_total?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                setSelectedOtaData(search);
                                setShowOtaModal(true);
                              }}
                            >
                              <Info className="h-4 w-4 text-slate-400" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!hotelSearches?.searches || hotelSearches.searches.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={12} className="text-center text-slate-400 py-8">
                            Nog geen zoekopdrachten geregistreerd. Zoekopdrachten verschijnen hier nadat gebruikers naar hotels zoeken.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                <div className="mt-4 p-3 bg-slate-900 rounded-lg text-sm text-slate-400">
                  <p><strong className="text-white">Legenda:</strong></p>
                  <ul className="mt-2 grid grid-cols-2 gap-1">
                    <li><span className="text-cyan-400">Nett (SH)</span> = Sunhotels inkoop prijs</li>
                    <li><span className="text-green-400">Markup (16%)</span> = Freestays marge</li>
                    <li><span className="text-white">Freestays</span> = Nett + Markup</li>
                    <li><span className="text-blue-400">FS -15%</span> = Freestays met 15% korting</li>
                    <li><span className="text-orange-400">BTW</span> = 21% over markup</li>
                    <li><span className="text-red-400">OTA Est.</span> = Geschatte OTA prijs</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Insights */}
            {hotelSearches?.insights && (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <h4 className="text-sm font-semibold text-white mb-2">Insights</h4>
                  <ul className="text-sm text-slate-400 space-y-1">
                    {hotelSearches.insights.map((insight, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <ArrowRight className="h-3 w-3 text-blue-400" />
                        {insight}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        
        {/* OTA PRICING TAB - New Real Scrape Data Analytics */}
        <TabsContent value="ota-pricing">
          <div className="space-y-6">
            {/* OTA Scraping Stats Overview */}
            {otaScrapeAnalytics && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-purple-900/50 to-slate-800 border-purple-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-300">Totaal Scrapes</p>
                        <p className="text-3xl font-bold text-white">{otaScrapeAnalytics.overview?.total_scrapes || 0}</p>
                        <p className="text-xs text-purple-400">Lifetime</p>
                      </div>
                      <BarChart3 className="h-10 w-10 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-900/50 to-slate-800 border-blue-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-300">Deze Week</p>
                        <p className="text-3xl font-bold text-white">{otaScrapeAnalytics.overview?.scrapes_this_week || 0}</p>
                        <p className="text-xs text-blue-400">Actieve monitoring</p>
                      </div>
                      <Search className="h-10 w-10 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-900/50 to-slate-800 border-green-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-300">Gem. Besparing</p>
                        <p className="text-3xl font-bold text-white">{otaScrapeAnalytics.savings?.avg_savings_percent || 0}%</p>
                        <p className="text-xs text-green-400">vs OTA platforms</p>
                      </div>
                      <TrendingUp className="h-10 w-10 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-900/50 to-slate-800 border-orange-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-orange-300">Success Rate</p>
                        <p className="text-3xl font-bold text-white">{otaScrapeAnalytics.overview?.success_rate || 0}%</p>
                        <p className="text-xs text-orange-400">Scraper accuracy</p>
                      </div>
                      <Check className="h-10 w-10 text-orange-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* OTA Prices Bar Chart */}
            {otaScrapeAnalytics?.by_ota && otaScrapeAnalytics.by_ota.length > 0 && (
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-green-400" />
                    Gemiddelde Prijzen per OTA Platform
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Vergelijking van gemiddelde prijzen tussen OTA's gebaseerd op {otaScrapeAnalytics.overview?.total_scrapes || 0} scrapes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={otaScrapeAnalytics.by_ota}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="ota" stroke="#94a3b8" fontSize={11} angle={-45} textAnchor="end" height={80} />
                        <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `€${v}`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                          formatter={(value) => [`€${value.toFixed(2)}`, 'Gem. Prijs']}
                        />
                        <Bar dataKey="avg_price" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* OTA Breakdown Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                    {otaScrapeAnalytics.by_ota.map((ota) => (
                      <div 
                        key={ota.ota} 
                        className="p-3 bg-slate-900 rounded-lg border border-slate-700"
                        style={{ borderLeftColor: getOtaColor(ota.ota), borderLeftWidth: '3px' }}
                      >
                        <p className="text-xs text-slate-400 truncate">{ota.ota}</p>
                        <p className="text-lg font-bold text-white">€{ota.avg_price}</p>
                        <p className="text-xs text-slate-500">{ota.scrape_count} scrapes</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Scrape Results Table */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Search className="h-5 w-5 text-green-400" />
                      OTA Scrape Resultaten
                      <Badge className="ml-2 bg-green-600">{otaScrapeResults.length} resultaten</Badge>
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Echte prijzen direct gescraped van OTA websites met Playwright
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      placeholder="Filter bestemming..."
                      value={scrapeDestFilter}
                      onChange={(e) => setScrapeDestFilter(e.target.value)}
                      className="px-3 py-1.5 text-sm bg-slate-900 border border-slate-600 rounded-md text-white placeholder-slate-500 w-40"
                    />
                    <label className="flex items-center gap-2 text-sm text-slate-400">
                      <input
                        type="checkbox"
                        checked={scrapeArchive}
                        onChange={(e) => setScrapeArchive(e.target.checked)}
                        className="rounded border-slate-600"
                      />
                      Archief
                    </label>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={fetchOtaScrapeData}
                      className="border-slate-600"
                    >
                      <RefreshCw className={`h-4 w-4 mr-1 ${otaScrapeLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={exportOtaScrapeCSV}
                      className="border-slate-600"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {otaScrapeLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div>
                  </div>
                ) : otaScrapeResults.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-300">Datum</TableHead>
                          <TableHead className="text-slate-300">Hotel</TableHead>
                          <TableHead className="text-slate-300">Bestemming</TableHead>
                          <TableHead className="text-right text-slate-300">Freestays</TableHead>
                          <TableHead className="text-right text-blue-400">Booking</TableHead>
                          <TableHead className="text-right text-orange-400">Kayak</TableHead>
                          <TableHead className="text-right text-cyan-400">Trivago</TableHead>
                          <TableHead className="text-right text-red-400">Hotels.com</TableHead>
                          <TableHead className="text-right text-green-400">Besparing</TableHead>
                          <TableHead className="text-center">Detail</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {otaScrapeResults.map((result, idx) => (
                          <TableRow key={result.result_id || idx} className="border-slate-700 hover:bg-slate-700/50">
                            <TableCell className="text-slate-400 text-xs font-mono">
                              {result.scraped_at ? new Date(result.scraped_at).toLocaleString('nl-NL', {
                                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                              }) : '-'}
                            </TableCell>
                            <TableCell className="text-white font-medium max-w-[180px] truncate" title={result.hotel_name}>
                              {result.hotel_name}
                            </TableCell>
                            <TableCell className="text-slate-300">{result.destination}</TableCell>
                            <TableCell className="text-right font-semibold text-white">
                              {result.freestays_price ? `€${result.freestays_price}` : '-'}
                            </TableCell>
                            <TableCell className="text-right text-blue-400">
                              {result.prices?.['Booking.com'] ? `€${result.prices['Booking.com']}` : '-'}
                            </TableCell>
                            <TableCell className="text-right text-orange-400">
                              {result.prices?.['Kayak'] || result.prices?.['Market Low'] ? 
                                `€${result.prices['Kayak'] || result.prices['Market Low']}` : '-'}
                            </TableCell>
                            <TableCell className="text-right text-cyan-400">
                              {result.prices?.['Trivago'] ? `€${result.prices['Trivago']}` : '-'}
                            </TableCell>
                            <TableCell className="text-right text-red-400">
                              {result.prices?.['Hotels.com'] ? `€${result.prices['Hotels.com']}` : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {result.savings_percent ? (
                                <Badge className="bg-green-900 text-green-400 border border-green-700">
                                  {result.savings_percent}%
                                </Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => {
                                  setSelectedScrapeDetail(result);
                                  setShowScrapeDetailModal(true);
                                }}
                              >
                                <Eye className="h-4 w-4 text-slate-400" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Search className="h-12 w-12 mx-auto text-slate-600 mb-4" />
                    <p className="text-slate-400">Geen scrape resultaten gevonden</p>
                    <p className="text-sm text-slate-500 mt-1">Start een nieuwe scrape via Admin → Price Compare</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Top Destinations */}
            {otaScrapeAnalytics?.top_destinations && otaScrapeAnalytics.top_destinations.length > 0 && (
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-purple-400" />
                    Top Bestemmingen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {otaScrapeAnalytics.top_destinations.map((dest, idx) => (
                      <div 
                        key={dest.destination} 
                        className="p-3 bg-slate-900 rounded-lg border border-slate-700"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs bg-purple-900 text-purple-300 px-1.5 py-0.5 rounded">#{idx + 1}</span>
                          <span className="text-white font-medium truncate">{dest.destination}</span>
                        </div>
                        <p className="text-sm text-slate-400">{dest.scrape_count} scrapes</p>
                        {dest.avg_savings_percent > 0 && (
                          <p className="text-xs text-green-400">Gem. {dest.avg_savings_percent}% besparing</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* SHARE ANALYTICS TAB */}
        <TabsContent value="shares">
          <div className="space-y-6">
            {/* Period Filter */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Share2 className="h-5 w-5 text-purple-400" /> Share Analytics
              </h2>
              <div className="flex gap-2">
                <Select value={shareDays.toString()} onValueChange={(v) => { setShareDays(parseInt(v)); fetchShareAnalytics(parseInt(v)); }}>
                  <SelectTrigger className="w-32 bg-slate-900 border-slate-600" data-testid="share-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 Dagen</SelectItem>
                    <SelectItem value="30">30 Dagen</SelectItem>
                    <SelectItem value="90">90 Dagen</SelectItem>
                    <SelectItem value="365">1 Jaar</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => fetchShareAnalytics()} className="bg-slate-900 border-slate-600" data-testid="share-refresh-btn">
                  <RefreshCw className={`h-4 w-4 ${shareLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {shareLoading && !shareData ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : (
              <>
                {/* Overview Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="share-stats-grid">
                  <Card className="bg-gradient-to-br from-purple-900/50 to-slate-800 border-purple-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-purple-300">Totaal Shares</p>
                          <p className="text-3xl font-bold text-white" data-testid="total-shares-count">{shareData?.total_shares || 0}</p>
                          <p className="text-xs text-purple-400">Laatste {shareDays} dagen</p>
                        </div>
                        <Share2 className="h-10 w-10 text-purple-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-green-900/50 to-slate-800 border-green-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-300">WhatsApp</p>
                          <p className="text-3xl font-bold text-white" data-testid="whatsapp-shares-count">{shareData?.by_method?.whatsapp || 0}</p>
                          <p className="text-xs text-green-400">Meest populair</p>
                        </div>
                        <MessageCircle className="h-10 w-10 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-blue-900/50 to-slate-800 border-blue-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-300">Referral Shares</p>
                          <p className="text-3xl font-bold text-white" data-testid="referral-shares-count">{shareData?.total_referral_shares || 0}</p>
                          <p className="text-xs text-blue-400">Met referral code</p>
                        </div>
                        <Crown className="h-10 w-10 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-orange-900/50 to-slate-800 border-orange-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-orange-300">Conversies</p>
                          <p className="text-3xl font-bold text-white" data-testid="share-conversions-count">{shareConversions?.total_signups_from_shares || 0}</p>
                          <p className="text-xs text-orange-400">Signups via shares</p>
                        </div>
                        <TrendingUp className="h-10 w-10 text-orange-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Shares by Platform (Pie) */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white">Shares per Platform</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[280px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(shareData?.by_method || {}).map(([name, value]) => ({ name, value }))}
                              cx="50%"
                              cy="50%"
                              innerRadius={55}
                              outerRadius={85}
                              paddingAngle={4}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                              {Object.entries(shareData?.by_method || {}).map(([name], index) => (
                                <Cell key={name} fill={SHARE_METHOD_COLORS[name] || COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      {/* Legend */}
                      <div className="flex flex-wrap gap-3 mt-2 justify-center">
                        {Object.entries(shareData?.by_method || {}).map(([name, count]) => (
                          <div key={name} className="flex items-center gap-1.5 text-sm">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SHARE_METHOD_COLORS[name] || '#6B7280' }} />
                            <span className="text-slate-300 capitalize">{name}</span>
                            <span className="text-slate-500">({count})</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Shares by Type (Pie) */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white">Shares per Type</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[280px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(shareData?.by_type || {}).map(([name, value]) => ({ name, value }))}
                              cx="50%"
                              cy="50%"
                              innerRadius={55}
                              outerRadius={85}
                              paddingAngle={4}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                              {Object.entries(shareData?.by_type || {}).map(([name], index) => (
                                <Cell key={name} fill={SHARE_TYPE_COLORS[name] || COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex flex-wrap gap-3 mt-2 justify-center">
                        {Object.entries(shareData?.by_type || {}).map(([name, count]) => (
                          <div key={name} className="flex items-center gap-1.5 text-sm">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SHARE_TYPE_COLORS[name] || '#6B7280' }} />
                            <span className="text-slate-300 capitalize">{name}</span>
                            <span className="text-slate-500">({count})</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Daily Shares Line Chart */}
                {shareData?.daily_shares && shareData.daily_shares.length > 0 && (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white">Dagelijkse Shares</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={shareData.daily_shares}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                            <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} labelStyle={{ color: '#fff' }} />
                            <Line type="monotone" dataKey="count" stroke="#a855f7" strokeWidth={2} dot={{ r: 3 }} name="Shares" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Top Sharers Table */}
                {shareData?.top_sharers && shareData.top_sharers.length > 0 && (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Users className="h-5 w-5 text-purple-400" /> Top Sharers
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow className="border-slate-700">
                            <TableHead className="text-slate-400">#</TableHead>
                            <TableHead className="text-slate-400">Gebruiker</TableHead>
                            <TableHead className="text-right text-slate-400">Aantal Shares</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {shareData.top_sharers.map((sharer, i) => (
                            <TableRow key={sharer.user_id} className="border-slate-700">
                              <TableCell className="text-slate-500">{i + 1}</TableCell>
                              <TableCell className="text-white font-mono text-sm">{sharer.user_id}</TableCell>
                              <TableCell className="text-right">
                                <Badge className="bg-purple-900 text-purple-300 border border-purple-700">{sharer.count}</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Referral Conversions */}
                {shareConversions?.conversions && shareConversions.conversions.length > 0 && (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-green-400" /> Referral Conversies
                      </CardTitle>
                      <CardDescription className="text-slate-400">
                        {shareConversions.total_codes_shared} codes gedeeld, {shareConversions.total_signups_from_shares} signups, {shareConversions.total_passes_from_shares} pass aankopen
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow className="border-slate-700">
                            <TableHead className="text-slate-400">Referral Code</TableHead>
                            <TableHead className="text-right text-slate-400">Keer Gedeeld</TableHead>
                            <TableHead className="text-right text-slate-400">Signups</TableHead>
                            <TableHead className="text-right text-slate-400">Pass Aankopen</TableHead>
                            <TableHead className="text-right text-slate-400">Conversie %</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {shareConversions.conversions.map((conv) => (
                            <TableRow key={conv.referral_code} className="border-slate-700">
                              <TableCell className="text-white font-mono font-medium">{conv.referral_code}</TableCell>
                              <TableCell className="text-right text-slate-300">{conv.times_shared}</TableCell>
                              <TableCell className="text-right">
                                <Badge className="bg-blue-900 text-blue-300">{conv.signups}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge className="bg-green-900 text-green-300">{conv.pass_purchases}</Badge>
                              </TableCell>
                              <TableCell className="text-right text-yellow-400 font-semibold">{conv.conversion_rate}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Empty State */}
                {(!shareData || shareData.total_shares === 0) && !shareLoading && (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="py-12 text-center">
                      <Share2 className="h-12 w-12 mx-auto text-slate-600 mb-4" />
                      <p className="text-slate-400 text-lg">Nog geen share data beschikbaar</p>
                      <p className="text-sm text-slate-500 mt-1">Share events worden automatisch geregistreerd wanneer gebruikers content delen via de ShareCard component</p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* OTA Details Modal */}
      <Dialog open={showOtaModal} onOpenChange={setShowOtaModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              OTA Prijsvergelijking
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedOtaData?.hotel_name}
            </DialogDescription>
          </DialogHeader>
          {selectedOtaData && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-900/30 rounded-lg border border-green-700">
                  <p className="text-sm text-slate-400">Freestays Prijs</p>
                  <p className="text-2xl font-bold text-green-400">€{selectedOtaData.display_prices?.freestays_total?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="p-4 bg-orange-900/30 rounded-lg border border-orange-700">
                  <p className="text-sm text-slate-400">OTA Gemiddelde</p>
                  <p className="text-2xl font-bold text-orange-400">€{selectedOtaData.ota_comparison?.average_price?.toFixed(2) || selectedOtaData.display_prices?.ota_estimated_total?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
              
              {/* OTA Sources List */}
              <div>
                <h4 className="font-medium text-white mb-3">Prijzen per OTA Platform</h4>
                <div className="space-y-2">
                  {selectedOtaData.ota_comparison?.prices_by_source && 
                    Object.entries(selectedOtaData.ota_comparison.prices_by_source).map(([source, price]) => (
                      <div key={source} className="flex items-center justify-between p-3 bg-slate-900 rounded-lg border border-slate-700">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-slate-500" />
                          <span className="text-white">{source}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold text-white">€{price?.toFixed(2) || '0.00'}</span>
                          {price > selectedOtaData.display_prices?.freestays_total && (
                            <Badge className="bg-green-900 text-green-400 border-green-700">
                              +€{(price - selectedOtaData.display_prices?.freestays_total).toFixed(2)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))
                  }
                  {(!selectedOtaData.ota_comparison?.prices_by_source || Object.keys(selectedOtaData.ota_comparison.prices_by_source).length === 0) && (
                    <p className="text-slate-400 text-center py-4">Geen OTA prijzen beschikbaar</p>
                  )}
                </div>
              </div>
              
              {/* Sources */}
              {selectedOtaData.ota_comparison?.sources && selectedOtaData.ota_comparison.sources.length > 0 && (
                <div>
                  <h4 className="font-medium text-white mb-2">Bronnen</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedOtaData.ota_comparison.sources.map((source) => (
                      <Badge key={source} className="bg-slate-700 text-slate-300">{source}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Data source indicator */}
              <div className={`p-3 rounded-lg ${selectedOtaData.is_real_ota_data ? 'bg-green-900/30 border border-green-700' : 'bg-yellow-900/30 border border-yellow-700'}`}>
                {selectedOtaData.is_real_ota_data ? (
                  <p className="text-sm text-green-400 flex items-center gap-2">
                    <Check className="h-4 w-4" />
                    <span><strong>Echte prijzen</strong> opgehaald via {selectedOtaData.ota_comparison?.data_source || 'API'}</span>
                  </p>
                ) : (
                  <p className="text-sm text-yellow-400 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    <span><strong>Geschatte prijzen</strong> - gebaseerd op typische OTA commissies (15-25%)</span>
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Scrape Result Detail Modal */}
      <Dialog open={showScrapeDetailModal} onOpenChange={setShowScrapeDetailModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-400" />
              Scrape Resultaat Detail
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedScrapeDetail?.hotel_name} - {selectedScrapeDetail?.destination}
            </DialogDescription>
          </DialogHeader>
          {selectedScrapeDetail && (
            <div className="space-y-4">
              {/* Hotel Info */}
              <div className="p-4 bg-slate-900 rounded-lg">
                <h3 className="font-semibold text-white text-lg">{selectedScrapeDetail.hotel_name}</h3>
                <p className="text-slate-400">{selectedScrapeDetail.destination}</p>
                <div className="flex gap-4 mt-2 text-sm text-slate-500">
                  <span>Check-in: {selectedScrapeDetail.check_in}</span>
                  <span>Check-out: {selectedScrapeDetail.check_out}</span>
                  <span>{selectedScrapeDetail.nights} nachten</span>
                </div>
              </div>
              
              {/* Price Cards */}
              <div className="grid grid-cols-2 gap-4">
                {selectedScrapeDetail.freestays_price && (
                  <div className="p-4 bg-green-900/30 rounded-lg border-2 border-green-600">
                    <p className="text-sm text-slate-400">Freestays</p>
                    <p className="text-3xl font-bold text-green-400">€{selectedScrapeDetail.freestays_price}</p>
                    <Badge className="mt-1 bg-green-600">Beste Prijs</Badge>
                  </div>
                )}
                {selectedScrapeDetail.best_ota_price && (
                  <div className="p-4 bg-orange-900/30 rounded-lg border border-orange-700">
                    <p className="text-sm text-slate-400">Beste OTA Prijs</p>
                    <p className="text-3xl font-bold text-orange-400">€{selectedScrapeDetail.best_ota_price}</p>
                  </div>
                )}
              </div>
              
              {/* All OTA Prices */}
              <div>
                <h4 className="font-medium text-white mb-3">Prijzen per OTA Platform</h4>
                <div className="grid grid-cols-2 gap-2">
                  {selectedScrapeDetail.prices && Object.entries(selectedScrapeDetail.prices).map(([ota, price]) => (
                    <div 
                      key={ota} 
                      className="flex items-center justify-between p-3 bg-slate-900 rounded-lg"
                      style={{ borderLeft: `3px solid ${getOtaColor(ota)}` }}
                    >
                      <span className="text-slate-300">{ota}</span>
                      <span className="font-bold text-white">€{price}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Savings */}
              {selectedScrapeDetail.savings_percent && (
                <div className="p-4 bg-green-900/30 rounded-lg border border-green-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Besparing vs beste OTA</p>
                      <p className="text-2xl font-bold text-green-400">
                        €{selectedScrapeDetail.savings_amount} ({selectedScrapeDetail.savings_percent}%)
                      </p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-green-500/50" />
                  </div>
                </div>
              )}
              
              {/* Meta Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Scrape ID</p>
                  <p className="font-mono text-xs text-slate-400">{selectedScrapeDetail.result_id}</p>
                </div>
                <div>
                  <p className="text-slate-500">Scrape Tijd</p>
                  <p className="text-slate-400">{selectedScrapeDetail.scraped_at ? new Date(selectedScrapeDetail.scraped_at).toLocaleString('nl-NL') : '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Duur</p>
                  <p className="text-slate-400">{selectedScrapeDetail.scrape_duration_ms ? `${(selectedScrapeDetail.scrape_duration_ms / 1000).toFixed(1)}s` : '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Proxy</p>
                  <p className="text-slate-400">{selectedScrapeDetail.proxy_used || 'Unknown'}</p>
                </div>
              </div>
              
              {/* Sources */}
              {selectedScrapeDetail.sources_success && selectedScrapeDetail.sources_success.length > 0 && (
                <div>
                  <p className="text-slate-500 mb-2">Succesvolle bronnen</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedScrapeDetail.sources_success.map((src, i) => (
                      <Badge key={i} className="bg-green-900 text-green-400">{src}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSAnalytics;
